import posthog from 'posthog-js';
import config from './config';
import { POSTHOG_PROJECT } from './shared/constants';
import { SignupSource } from '../models/shared/User';

// Product analytics (PostHog). The same project is used on the marketing site (www.arbtask.com,
// repo arbtask-web, public/js/analytics.js), which also owns the cookie consent banner.
//
// Consent: the marketing site mirrors the visitor's choice into a cookie on .arbtask.com.
//   granted -> normal persistence; PostHog's cookie is shared across *.arbtask.com, so the
//              visitor's marketing history is merged into their user on identify()
//   otherwise (rejected, never asked, native app) -> in-memory only, nothing stored on the device.
//              Logged-in users are still identified, so app usage and backend events line up.

const CONSENT_COOKIE = 'arbtask_cookie_consent';

const isEnabled = !config.useEmulators;

const hasCookieConsent = () => {
    return document.cookie.split(';').some(c => c.trim() === `${CONSENT_COOKIE}=granted`);
};

export const initAnalytics = () => {
    if (!isEnabled) {
        return;
    }
    posthog.init(POSTHOG_PROJECT.key, {
        api_host: POSTHOG_PROJECT.host,
        // Pinned snapshot of SDK defaults (includes SPA pageview tracking on history changes)
        defaults: '2026-08-30',
        // Anonymous visitors (e.g. customers opening a shared offer) don't create person profiles
        person_profiles: 'identified_only',
        persistence: hasCookieConsent() ? 'localStorage+cookie' : 'memory',
    });
};

export const identifyUser = (uid: string, email: string) => {
    if (!isEnabled) {
        return;
    }
    posthog.identify(uid, { email });
};

export const setAnalyticsOrganization = (orgId: string, properties: Record<string, unknown>) => {
    if (!isEnabled) {
        return;
    }
    posthog.group('organization', orgId, properties);
};

// Internal users (super admins, impersonation) shouldn't skew the numbers
export const excludeFromAnalytics = () => {
    if (!isEnabled) {
        return;
    }
    posthog.opt_out_capturing();
};

export const resetAnalytics = () => {
    if (!isEnabled) {
        return;
    }
    posthog.reset();
};

// Marketing site links to the app carry ?src_page=...&utm_*=... Read once at boot (before any
// redirect drops the query) and kept in memory only; sent with the login request so the backend
// can store it on the new user.
const SIGNUP_SOURCE_PARAMS: (keyof SignupSource)[] = ['src_page', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

const readSignupSource = (): SignupSource | undefined => {
    const params = new URLSearchParams(window.location.search);
    const source: SignupSource = {};
    for (const key of SIGNUP_SOURCE_PARAMS) {
        const value = params.get(key);
        if (value) {
            source[key] = value;
        }
    }
    return Object.keys(source).length ? source : undefined;
};

export const initialSignupSource = readSignupSource();
